import {
  Block,
  Campaign,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Search,
  Store,
  VisibilityOff,
} from "@mui/icons-material";
import {
  Box,
  Chip,
  Fade,
  IconButton,
  InputAdornment,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { useToastStore } from "../../../stores";
import { colorPrimary } from "../../../theme";
import { fetchConcessionariaSintetico } from "../repository/AdminRepository";
import { ConcessionariaSinteticoData, ConcessionariaSinteticoItem } from "../types/types";

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const AnimatedBox = styled.div<{ $delay?: number }>`
  animation: ${slideUp} 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  animation-delay: ${({ $delay }) => $delay ?? 0}ms;
  opacity: 0;
`;

interface KpiCardProps {
  label: string;
  value: string;
  subtitle: string;
  icon: React.ReactNode;
  accentColor: string;
  bgColor: string;
  delay: number;
}

function KpiCard({ label, value, subtitle, icon, accentColor, bgColor, delay }: KpiCardProps) {
  return (
    <AnimatedBox $delay={delay}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          position: "relative",
          overflow: "hidden",
          border: "1px solid #f1f5f9",
          height: "100%",
          transition: "all 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: "0 12px 40px rgba(0,0,0,0.08)",
            borderColor: accentColor + "40",
          },
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Typography
            fontSize="0.7rem"
            fontWeight={700}
            textTransform="uppercase"
            letterSpacing={1.5}
            color="#94a3b8"
          >
            {label}
          </Typography>
          <Box
            sx={{
              bgcolor: bgColor,
              color: accentColor,
              p: 1,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {icon}
          </Box>
        </Box>
        <Box display="flex" alignItems="baseline" gap={1}>
          <Typography
            fontSize="2.5rem"
            fontWeight={900}
            letterSpacing={-2}
            color={accentColor}
            lineHeight={1}
          >
            {value}
          </Typography>
          <Typography fontSize="0.75rem" fontWeight={600} color={accentColor + "99"}>
            {subtitle}
          </Typography>
        </Box>
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            width: "100%",
            height: 3,
            bgcolor: accentColor + "30",
          }}
        />
      </Paper>
    </AnimatedBox>
  );
}

function KpiCardSkeleton({ delay }: { delay: number }) {
  return (
    <AnimatedBox $delay={delay}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          position: "relative",
          overflow: "hidden",
          border: "1px solid #f1f5f9",
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Skeleton variant="text" width={100} height={16} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rounded" width={36} height={36} sx={{ borderRadius: 2 }} />
        </Box>
        <Box display="flex" alignItems="baseline" gap={1}>
          <Skeleton variant="text" width={80} height={48} sx={{ borderRadius: 1 }} />
          <Skeleton variant="text" width={60} height={14} sx={{ borderRadius: 1 }} />
        </Box>
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            width: "100%",
            height: 3,
            bgcolor: "#e2e8f0",
          }}
        />
      </Paper>
    </AnimatedBox>
  );
}

function TableRowSkeleton() {
  return (
    <TableRow>
      {Array.from({ length: 10 }).map((_, i) => (
        <TableCell key={i} sx={{ py: 2.2, borderBottom: "1px solid #f8fafc" }}>
          <Skeleton
            variant="text"
            width={i === 1 ? 160 : i === 2 ? 140 : i === 4 ? 120 : 70}
            height={20}
            sx={{ borderRadius: 1 }}
          />
        </TableCell>
      ))}
    </TableRow>
  );
}

const ITEMS_PER_PAGE = 10;

function formatNumber(val: number | null): string {
  if (val == null) return "0";
  return val.toLocaleString("pt-BR");
}

export function ConcessionariaSinteticoTab() {
  const [data, setData] = useState<ConcessionariaSinteticoData | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const { setToast } = useToastStore();
  const jaCarregouRef = useRef(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const result = await fetchConcessionariaSintetico();
      if (!result?.success) {
        setToast({ variant: "error", message: result.message });
        return;
      }
      setData(result.data);
    } catch {
      setToast({ variant: "error", message: "Erro. Contate o administrador." });
    } finally {
      setLoading(false);
    }
  }, [setToast]);

  useEffect(() => {
    if (jaCarregouRef.current) return;
    jaCarregouRef.current = true;
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered: ConcessionariaSinteticoItem[] = (data?.concessionarias ?? []).filter((c) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      c.concessionaria_label.toLowerCase().includes(q) ||
      c.grupo_label.toLowerCase().includes(q) ||
      c.documento.toLowerCase().includes(q) ||
      c.cidade.toLowerCase().includes(q) ||
      c.responsavel.toLowerCase().includes(q) ||
      c.status.toLowerCase().includes(q)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));
  const paginated = filtered.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  const handleSearchChange = (val: string) => {
    setSearch(val);
    setCurrentPage(1);
  };

  return (
    <Fade in timeout={400}>
      <Box display="flex" flexDirection="column" gap={4}>
        {/* KPI CARDS */}
        <Box
          display="grid"
          gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr 1fr", md: "repeat(5, 1fr)" }}
          gap={2.5}
        >
          {loading ? (
            <>
              <KpiCardSkeleton delay={80} />
              <KpiCardSkeleton delay={160} />
              <KpiCardSkeleton delay={240} />
              <KpiCardSkeleton delay={320} />
              <KpiCardSkeleton delay={400} />
            </>
          ) : (
            <>
              <KpiCard
                label="Total Concessionárias"
                value={formatNumber(data?.total_qtd_concessionaria ?? null)}
                subtitle="cadastradas"
                icon={<Store />}
                accentColor="#0284c7"
                bgColor="#e0f2fe"
                delay={80}
              />
              <KpiCard
                label="Ativas"
                value={formatNumber(data?.total_qtd_concessionaria_ativa ?? null)}
                subtitle="concessionárias"
                icon={<CheckCircle />}
                accentColor="#10b981"
                bgColor="#ecfdf5"
                delay={160}
              />
              <KpiCard
                label="Inativas"
                value={formatNumber(data?.total_qtd_concessionaria_inativa ?? null)}
                subtitle="concessionárias"
                icon={<Block />}
                accentColor="#ef4444"
                bgColor="#fef2f2"
                delay={240}
              />
              <KpiCard
                label="Ativas c/ Publicação"
                value={formatNumber(data?.total_qtd_concessionaria_ativa_com_publicacao ?? null)}
                subtitle="publicando"
                icon={<Campaign />}
                accentColor="#0891b2"
                bgColor="#ecfeff"
                delay={320}
              />
              <KpiCard
                label="Ativas s/ Publicação"
                value={formatNumber(data?.total_qtd_concessionaria_ativa_sem_publicacao ?? null)}
                subtitle="sem anúncios"
                icon={<VisibilityOff />}
                accentColor="#f59e0b"
                bgColor="#fffbeb"
                delay={400}
              />
            </>
          )}
        </Box>

        {/* TABELA */}
        <AnimatedBox $delay={300}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 4,
              overflow: "hidden",
              border: "1px solid #f1f5f9",
              boxShadow: "0 4px 24px rgba(0,0,0,0.04)",
            }}
          >
            <Box
              px={3.5}
              py={3}
              display="flex"
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", md: "center" }}
              flexDirection={{ xs: "column", md: "row" }}
              gap={2}
            >
              <Box>
                <Typography fontWeight={800} fontSize="1.15rem" color="#1e293b">
                  Concessionárias
                </Typography>
                <Typography fontSize="0.82rem" color="#94a3b8" fontWeight={500}>
                  Listagem de todas as concessionárias cadastradas
                </Typography>
              </Box>
              <TextField
                size="small"
                placeholder="Buscar..."
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ fontSize: 18, color: "#94a3b8" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  minWidth: 260,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    fontSize: "0.85rem",
                    bgcolor: "#f8fafc",
                    "& fieldset": { borderColor: "#e2e8f0" },
                    "&:hover fieldset": { borderColor: "#94a3b8" },
                    "&.Mui-focused fieldset": { borderColor: colorPrimary },
                  },
                }}
              />
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f8fafc" }}>
                    {[
                      { label: "ID" },
                      { label: "Grupo" },
                      { label: "Concessionária" },
                      { label: "Cidade" },
                      { label: "Responsável" },
                      { label: "Status" },
                      { label: "Publicados", align: "right" as const },
                      { label: "Vendidos", align: "right" as const },
                      { label: "Não Vendidos", align: "right" as const },
                      { label: "Usuários Ativos", align: "right" as const },
                    ].map((col) => (
                      <TableCell
                        key={col.label}
                        align={col.align ?? "left"}
                        sx={{
                          fontSize: "0.68rem",
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: 1.5,
                          color: "#94a3b8",
                          borderBottom: "2px solid #f1f5f9",
                          py: 2,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {col.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    Array.from({ length: 5 }).map((_, i) => <TableRowSkeleton key={i} />)
                  ) : paginated.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} sx={{ py: 6, textAlign: "center" }}>
                        <Typography fontSize="0.9rem" color="#94a3b8" fontWeight={500}>
                          Nenhuma concessionária encontrada
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginated.map((row) => (
                      <TableRow
                        key={row.num_id_concessionaria}
                        sx={{
                          transition: "all 0.2s",
                          "&:hover": { bgcolor: colorPrimary + "08" },
                          "& td": { borderBottom: "1px solid #f8fafc" },
                        }}
                      >
                        <TableCell sx={{ fontSize: "0.85rem", color: "#94a3b8", fontWeight: 600, py: 2.2 }}>
                          #{row.num_id_concessionaria}
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.85rem", color: "#64748b", py: 2.2 }}>
                          {row.grupo_label}
                        </TableCell>
                        <TableCell sx={{ py: 2.2 }}>
                          <Typography fontSize="0.85rem" fontWeight={700} color="#1e293b" lineHeight={1.3}>
                            {row.concessionaria_label}
                          </Typography>
                          <Typography fontSize="0.72rem" color="#94a3b8" fontFamily="monospace" mt={0.3}>
                            {row.documento}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.85rem", color: "#64748b", py: 2.2 }}>
                          {row.cidade}
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.85rem", color: "#64748b", py: 2.2 }}>
                          {row.responsavel}
                        </TableCell>
                        <TableCell sx={{ py: 2.2 }}>
                          <Chip
                            label={row.status}
                            size="small"
                            sx={{
                              fontWeight: 700,
                              fontSize: "0.7rem",
                              height: 24,
                              ...(row.status.toLowerCase() === "ativo"
                                ? {
                                    bgcolor: "#ecfdf5",
                                    color: "#10b981",
                                    border: "1px solid #10b98130",
                                  }
                                : {
                                    bgcolor: "#fef2f2",
                                    color: "#ef4444",
                                    border: "1px solid #ef444430",
                                  }),
                            }}
                          />
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: "0.85rem", fontWeight: 600, color: "#1e293b", py: 2.2 }}>
                          {formatNumber(row.qtd_anuncio_publicado)}
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: "0.85rem", fontWeight: 600, color: "#10b981", py: 2.2 }}>
                          {formatNumber(row.qtd_vendido)}
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: "0.85rem", fontWeight: 600, color: "#ef4444", py: 2.2 }}>
                          {formatNumber(row.qtd_nao_vendido)}
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            fontSize: "0.85rem",
                            fontWeight: 600,
                            color: row.qtd_usuario_ativo === 0 ? "#94a3b8" : "#1e293b",
                            py: 2.2,
                          }}
                        >
                          {formatNumber(row.qtd_usuario_ativo)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <Box
              px={3.5}
              py={2.5}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              borderTop="1px solid #f1f5f9"
              bgcolor="#fafbfc"
            >
              <Typography fontSize="0.82rem" color="#94a3b8" fontWeight={500}>
                Mostrando {paginated.length} de {filtered.length} resultados
              </Typography>
              <Box display="flex" alignItems="center" gap={0.5}>
                <IconButton
                  size="small"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  sx={{ color: "#94a3b8" }}
                >
                  <ChevronLeft />
                </IconButton>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let page: number;
                  if (totalPages <= 5) {
                    page = i + 1;
                  } else if (currentPage <= 3) {
                    page = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    page = totalPages - 4 + i;
                  } else {
                    page = currentPage - 2 + i;
                  }
                  return (
                    <Box
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "0.8rem",
                        fontWeight: 700,
                        cursor: "pointer",
                        transition: "all 0.2s",
                        ...(currentPage === page
                          ? {
                              bgcolor: colorPrimary,
                              color: "white",
                              boxShadow: `0 2px 8px ${colorPrimary}40`,
                            }
                          : {
                              color: "#64748b",
                              "&:hover": { bgcolor: "#f1f5f9" },
                            }),
                      }}
                    >
                      {page}
                    </Box>
                  );
                })}
                <IconButton
                  size="small"
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  sx={{ color: "#94a3b8" }}
                >
                  <ChevronRight />
                </IconButton>
              </Box>
            </Box>
          </Paper>
        </AnimatedBox>
      </Box>
    </Fade>
  );
}
